import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

const DISMISSED_KEY = 'galago-install-prompt-dismissed';
const INSTALLED_KEY = 'galago-pwa-installed';

function isRunningStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
}

function isIOSDevice() {
  const { userAgent, platform, maxTouchPoints } = window.navigator;
  return /iphone|ipad|ipod/i.test(userAgent)
    || (platform === 'MacIntel' && maxTouchPoints > 1);
}

export default function PWAInstallPrompt() {
  const { pathname } = useLocation();
  // Marketing pages and design previews should not be interrupted by install UI.
  // Keep installation available elsewhere without marking it dismissed.
  const suppressPrompt = pathname === '/'
    || ['/dashboard/e-invite', '/dashboard/theme', '/dashboard/showcase', '/account/showcase'].includes(pathname)
    || pathname.startsWith('/e/')
    || pathname.startsWith('/showcase/');
  const [installEvent, setInstallEvent] = useState(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [visible, setVisible] = useState(false);

  const setPromptVisible = (next) => {
    setVisible(next);
  };

  useEffect(() => {
    const shown = visible && !suppressPrompt;
    document.body.dataset.galagoInstallPrompt = shown ? 'visible' : 'hidden';
    window.dispatchEvent(new CustomEvent('galago-install-prompt-change', { detail: { visible: shown } }));
  }, [visible, suppressPrompt]);

  useEffect(() => {
    if (isRunningStandalone()) {
      localStorage.setItem(INSTALLED_KEY, 'true');
      return undefined;
    }

    const ios = isIOSDevice();
    const knownInstalled = localStorage.getItem(INSTALLED_KEY) === 'true';
    const dismissedThisSession = sessionStorage.getItem(DISMISSED_KEY) === 'true';
    let iosTimer;

    if (ios && !knownInstalled && !dismissedThisSession) {
      iosTimer = window.setTimeout(() => {
        setShowIOSInstructions(true);
        setPromptVisible(true);
      }, 1200);
    }

    const handleInstallPrompt = (event) => {
      event.preventDefault();
      // Receiving this event means the browser considers the PWA installable,
      // so clear any stale marker left after an uninstall.
      localStorage.removeItem(INSTALLED_KEY);
      setInstallEvent(event);
      if (!dismissedThisSession) setPromptVisible(true);
    };

    const handleInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, 'true');
      setInstallEvent(null);
      setPromptVisible(false);
    };

    window.addEventListener('beforeinstallprompt', handleInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.clearTimeout(iosTimer);
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setPromptVisible(false);
  };

  const install = async () => {
    if (!installEvent) return;

    await installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    setInstallEvent(null);

    if (outcome === 'accepted') {
      localStorage.setItem(INSTALLED_KEY, 'true');
      setPromptVisible(false);
    }
  };

  if (!visible || suppressPrompt) return null;

  const iconUrl = `${process.env.PUBLIC_URL}/assets/pwa-icon-192.png?v=2`;

  return (
    <aside
      aria-label="Install GalaGo"
      className="fixed bottom-4 left-4 right-4 z-[300] mx-auto max-w-md rounded-2xl border border-white/15 bg-[#171717]/95 p-4 text-white shadow-2xl backdrop-blur-xl sm:left-auto sm:right-6 sm:w-[380px]"
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="absolute right-3 top-3 rounded-full p-1.5 text-white/60 transition hover:bg-white/10 hover:text-white"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex gap-3 pr-7">
        <img
          src={iconUrl}
          alt=""
          className="h-12 w-12 flex-none rounded-xl"
        />
        <div>
          <p className="font-display text-base font-bold">Add GalaGo to your Home Screen</p>
          {showIOSInstructions ? (
            <>
              <p className="mt-1 text-sm leading-5 text-white/70">
                Tap <Share className="mx-1 inline h-4 w-4" aria-label="Share" /> Share, then choose <strong className="text-white">Add to Home Screen</strong>.
              </p>
              <button
                type="button"
                className="mt-2 text-xs font-semibold text-[#00C9A7] hover:underline"
                onClick={() => {
                  localStorage.setItem(INSTALLED_KEY, 'true');
                  setPromptVisible(false);
                }}
              >
                I’ve added it
              </button>
            </>
          ) : (
            <p className="mt-1 text-sm leading-5 text-white/70">
              Launch your event companion directly from your phone or desktop.
            </p>
          )}
        </div>
      </div>

      {installEvent && (
        <button
          type="button"
          onClick={install}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#00C9A7] px-4 py-2.5 text-sm font-bold text-[#0d0d0d] transition hover:bg-[#20d8b9] focus:outline-none focus:ring-2 focus:ring-[#00C9A7] focus:ring-offset-2 focus:ring-offset-[#171717]"
        >
          <Download className="h-4 w-4" />
          Install GalaGo
        </button>
      )}
    </aside>
  );
}
